import { Award, Clock, BookOpen } from "lucide-react";
import { SectionTitle } from "../utils/helpers.jsx";

export default function LearningProgress({ stats, isTableView = false }) {
  if (!stats) return null;

  // If table view is enabled, render the table format
  if (isTableView) {
    // Calculate band based on overall percentage
    const getBand = (percentage) => {
      if (percentage >= 75) return "Excellent";
      if (percentage >= 50) return "Good";
      if (percentage >= 25) return "Average";
      return "Needs Improvement";
    };

    // Get corresponding band class
    const getBandClass = (percentage) => {
      if (percentage >= 75) return "bg-green-100 text-green-800";
      if (percentage >= 50) return "bg-blue-100 text-blue-800";
      if (percentage >= 25) return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    };

    return (
      <div className="mb-6 md:mb-8">
        <SectionTitle title="📈 Overall Course Learning Progress" />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Total Courses
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Total Time Spent
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Lessons Completed
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Course Status
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Overall Progress
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600">
                  Band
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-3 text-center border">
                  {stats.totalCourses}
                </td>
                <td className="px-4 py-3 text-center border">
                  {stats.formattedTimeSpent}
                </td>
                <td className="px-4 py-3 text-center border">
                  {stats.totalCompletedLessons} / {stats.totalLessons}
                </td>
                <td className="px-4 py-3 text-center border">
                  {stats.completedCourses} completed
                </td>
                <td className="px-4 py-3 text-center border">
                  {stats.overallPercentage}%
                </td>
                <td className="px-4 py-3 border">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getBandClass(
                      stats.overallPercentage
                    )}`}
                  >
                    {getBand(stats.overallPercentage)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          This overview summarizes the student's overall learning progress
          across all enrolled courses.
        </p>
      </div>
    );
  }

  // Original card view
  return (
    <div className="p-4 my-3 mb-6 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 md:mb-8">
      <h3 className="mb-3 font-medium text-gray-700 text-md">
        Overall Course Learning Progress
      </h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
        {/* Overall Completion Rate */}
        <div className="flex items-center p-3 space-x-3 bg-white rounded-md shadow-sm">
          <div className="p-2 bg-blue-100 rounded-full">
            <Award className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Completion Rate</p>
            <p className="text-lg font-semibold">{stats.overallPercentage}%</p>
          </div>
        </div>

        {/* Total Time Spent */}
        <div className="flex items-center p-3 space-x-3 bg-white rounded-md shadow-sm">
          <div className="p-2 bg-green-100 rounded-full">
            <Clock className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Time Spent</p>
            <p className="text-lg font-semibold">{stats.formattedTimeSpent}</p>
          </div>
        </div>

        {/* Lessons Progress */}
        <div className="flex items-center p-3 space-x-3 bg-white rounded-md shadow-sm">
          <div className="p-2 bg-yellow-100 rounded-full">
            <BookOpen className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Lessons Completed</p>
            <p className="text-lg font-semibold">
              {stats.totalCompletedLessons} / {stats.totalLessons}
            </p>
          </div>
        </div>

        {/* Course Status */}
        <div className="flex items-center p-3 space-x-3 bg-white rounded-md shadow-sm">
          <div className="p-2 bg-indigo-100 rounded-full">
            <BookOpen className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Course Status</p>
            <p className="text-lg font-semibold">
              {stats.completedCourses} / {stats.totalCourses} Completed
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex justify-between mb-1 text-xs text-gray-500">
          <span>Overall Progress</span>
          <span>{stats.overallPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${
              stats.overallPercentage >= 75
                ? "bg-green-500"
                : stats.overallPercentage >= 50
                ? "bg-blue-500"
                : stats.overallPercentage >= 25
                ? "bg-yellow-500"
                : "bg-red-500"
            }`}
            style={{ width: `${stats.overallPercentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
}
